import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Grid, Button } from '@material-ui/core';

import SatelliteCard from './SatelliteCard';
import { CHOICE_GRID_MAX_WIDTH } from '../../../theme';
import SatelliteNotice from './SatelliteNotice';
import { NEXT, clearAcquisitionImageCache } from '../../../hooks/useStepNavigations';
import { getStandardSatellites, getCombinedSatellite } from '../../../common/satellites';
import { Actions as Acquisition } from '../../../store/ducks/acquisition';
import ReactGA from 'react-ga';
import { useTranslation } from 'react-i18next';
import TourGuider from '../tour/TourGuider';
import { useLocalStorage } from '../../../common/utils';
import HelpButton from '../core/HelpButton';
import { push } from 'connected-react-router';
import { useFile, useResult } from '../../contexts';

/**
 * Module for components shared across all modules.
 * @module Shared
 */

/**
 * @constant SATELLITE_GRID_MAX_WIDTH
 * @see {@link CHOICE_GRID_MAX_WIDTH} - the shared rule this is an alias of, kept under its own name
 * because the notice below the cards lines up with it.
 * @description Widest the card row may get, in px. The wizards do not agree on how wide their card
 * is - some are capped near 1000px, others are a fraction of the viewport and end up half again as
 * wide on a large screen - so leaving the cards to fill their container made the same two cards come
 * out at very different sizes from one module to the next. Capping the row here settles it in one
 * place, and centring it keeps the pair under the step indicator above.
 * @memberof module:Shared
 */
export const SATELLITE_GRID_MAX_WIDTH = CHOICE_GRID_MAX_WIDTH;

/**
 * @function SatelliteChooser
 * @description Component for choosing a satellite for data acquisition. It gets the satellite name and provider,
 * saves the selection to the Redux store, and allows navigation to the next step in the acquisition process.
 * @param {Object} props - Component props.
 * @param {Function} props.navigate - Function to navigate to the next step.
 * @param {Function} props.specifySatellites - Function to specify satellite options (e.g., for Landsat collections).
 * *Called by:
 * @see {Bathymetry} - bathymetry's acquisition (index.jsx)
 * @returns {JSX.Element} The SatelliteChooser component.
 */
const SatelliteChooser = ({ navigate, specifySatellites, sessionImportButton, variant = 'classic' }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [t] = useTranslation();
  let url = window.location.href;
  const satellites = getStandardSatellites(url.indexOf('intertidal') !== -1 ? true : false);
  // Shore-S only (route /main/shore_s): third card combining both satellites,
  // dispatched as index 2 (COMBINED_SATELLITE_INDEX in common/satellites.js).
  /**
   * Whether this module offers the two satellites as one option, which only Shore-S does. The
   * guidance under the cards says what that third card means wherever it is on screen.
   */
  const hasCombinedSatellite = url.indexOf('shore_s') !== -1;
  if (hasCombinedSatellite) satellites.push(getCombinedSatellite(false));
  satellites.forEach((el) => (el.enabled = true));
  const [isTourOpen, setIsTourOpen] = useLocalStorage('showSatelliteTour', true);
  const landsatCollectionOptions = url.indexOf('bathymetry') !== -1 ? ['LT05', 'LC08'] : [];
  const { data, setData } = useResult();
  const { setFileData } = useFile();
  const isModern = variant === 'modern';

  const steps = [
    {
      selector: '#satellitechooser',
      content: t('tour.acquisition.1.info'),
    },
    {
      selector: '#Landsat_props',
      content: t('tour.acquisition.1.Landsat.info'),
    },
    {
      selector: '#Sentinel-2_props',
      content: t('tour.acquisition.1.Sentinel-2.info'),
    },
  ];

  /**
   * @function hasSatelliteChanged
   * @description Whether this choice differs from the one already in effect. Read before the
   * choice is dispatched, since dispatching is what overwrites it.
   * @param {Number} index - The chosen satellite's position in the list.
   * @param {String} type - Its processing level, TOA or SR.
   * @memberof module:Shared
   * @returns {Boolean} False the first time through, when there is nothing to have changed from.
   */
  const hasSatelliteChanged = (index, type) => {
    const previousIndex = window.sessionStorage.getItem('selectedSatelliteIndex');
    if (previousIndex === null) return false;
    return previousIndex !== String(index) || window.sessionStorage.getItem('typeSatellite') !== type;
  };

  /**
   * @function forgetPreviousSatellite
   * @description Drops everything the previous satellite produced: the image lists already found,
   * the dates and period narrowed down from them, and the thumbnails and composite the period
   * step built.
   * None of it survives a change of satellite, but nothing used to clear it either - the cache is
   * keyed by area and period, which both stay the same - so choosing Sentinel after Landsat left
   * Landsat scenes listed and selected under Sentinel. The area itself is deliberately kept: the
   * same stretch of coast seen by another sensor is the usual reason to come back here.
   * @memberof module:Shared
   * @returns {void}
   */
  const forgetPreviousSatellite = () => {
    clearAcquisitionImageCache();
    dispatch(Acquisition.setAvailableDates([]));
    dispatch(Acquisition.setPeriod(null, null));
    setFileData((prev) => ({ ...prev, periodState: undefined }));
  };

  /**
   * @function handleChoice
   * @description Handles the satellite choice by the user, dispatches the selection to the Redux store,
   * and navigates to the next step in the acquisition process. Also logs the event using ReactGA for Google Analytics.
   * @param {number} index - Index of the chosen satellite from the satellites array.
   * @memberof module:Shared
   * @returns {void}
   */
  const handleChoice = (index, type = 'TOA', landsatOptions = [false, false]) => {
    ReactGA.event({
      category: 'Acquisition',
      action: 'ChooseSatellite',
      value: index,
      typeMission: type,
    });
    if (index === 1 && ['csqueeze', 'euscoast', 'intertidal'].some((module) => url.indexOf(module) !== -1)) type = 'SR';
    if (url.indexOf('bathymetry') !== -1) {
      type = 'SR';
      const landsatOptionsBooltoString = [landsatOptions[0] ? 'LT05' : '', landsatOptions[1] ? 'LC08' : ''];
      setData({ ...data, landsatChosenCollections: landsatOptionsBooltoString });
    }
    if (hasSatelliteChanged(index, type)) forgetPreviousSatellite();
    dispatch(Acquisition.setSatellite(index, type, index === 1 && url.indexOf('intertidal') !== -1));
    navigate(NEXT);
  };

  return (
    <Box>
      <HelpButton
        onClickFunction={() => {
          setIsTourOpen(true);
        }}
        extra={sessionImportButton}
      />
      <Grid container spacing={3} justifyContent="center" id="satellitechooser" className={classes.grid}>
        {satellites.map((satellite, i) => (
          <Grid item key={i} xs={isModern ? 6 : 4} id={satellite.name}>
            <SatelliteCard
              name={satellite.name}
              provider={satellite.provider}
              image={satellite.image}
              cycle={satellite.summary.cycle}
              startYear={satellite.summary.startYear}
              endYear={satellite.summary.endYear}
              resolution={satellite.summary.opticalResolution}
              onChoose={(type = 'TOA', landsatColSwitchState) => handleChoice(i, type, landsatColSwitchState)}
              enabled={satellite.enabled}
              specifySatellites={specifySatellites}
              landsatCollectionOptions={landsatCollectionOptions}
              variant={variant}
              description={isModern ? t(`forms.acquisition.1.card.about.${satellite.name}`, { defaultValue: '' }) : undefined}
            />
          </Grid>
        ))}
        <TourGuider steps={steps} isOpen={isTourOpen} setIsTourOpen={setIsTourOpen} />
      </Grid>
      {isModern && <SatelliteNotice includeCombined={hasCombinedSatellite} />}
      <Box textAlign="center">
        <Button
          variant="contained"
          onClick={() => {
            dispatch(push('/main/selection'));
          }}
          className={classes.button}>
          {t('forms.acquisition.prev')}
        </Button>
      </Box>
    </Box>
  );
};

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Defines CSS styles .
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:Shared
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  content: {
    '&:not(:first-child)': {
      borderLeft: `1px solid ${theme.palette.divider}`,
    },
  },
  // Grid v4's `spacing` makes the container `calc(100% + 24px)` wide,
  // compensated by negative margins — overriding the margin without fixing
  // the width made the container overflow its parent (visible once a third
  // card filled all 12 columns). Padding + full width keeps it contained.
  grid: {
    margin: '0 auto',
    width: '100%',
    maxWidth: SATELLITE_GRID_MAX_WIDTH,
    padding: theme.spacing(2),
  },
  /**
   * Advisory strip under the cards: a tinted plate with an icon, for guidance that helps the
   * choice without competing with it. Deliberately not a MUI Alert - nothing here went wrong, and
   * Alert's severity colours would read as a warning.
   */
  button: {
    marginBottom: theme.spacing(4),
  },
}));

export default SatelliteChooser;
